import { useMemo } from "react"

import { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { api } from "~/utils/api"
import {
  convertToStartOfMonthUTC,
  getYearUTC,
  mapToSeason,
  startOfTodayUTC,
} from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"

import { useChartSeriesConfig, useYearSeriesStyle } from "~/constants/colors"

type Props = {
  variantIds: string[]
  warehouseId: string | undefined
  channel: string | undefined
  selectedYears: number[]
  referenceYear: number
  enabled: boolean
  monthly?: boolean
}

/**
 * Aggregate weekly sales data into monthly buckets
 */
function aggregateToMonthly(
  sales: { date: Date; value: number }[],
): { date: Date; value: number }[] {
  const monthlyMap = new Map<string, { date: Date; value: number }>()

  for (const sale of sales) {
    const monthStart = convertToStartOfMonthUTC(sale.date)
    const key = monthStart.toISOString()

    const existing = monthlyMap.get(key)
    if (existing) {
      existing.value += sale.value
    } else {
      monthlyMap.set(key, { date: monthStart, value: sale.value })
    }
  }

  return Array.from(monthlyMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime())
}

export function useYearComparisonGraph({
  variantIds,
  warehouseId,
  channel,
  selectedYears,
  referenceYear,
  enabled,
  monthly = true,
}: Props) {
  const chartConfig = useChartSeriesConfig()
  const getYearSeriesStyle = useYearSeriesStyle()
  // Use useQueries to fetch all years in a single hook call (avoids rules of hooks violation)
  const yearQueries = api.useQueries((t) =>
    selectedYears.map((year) => {
      const startDate = new Date(Date.UTC(year, 0, 1))
      const endDate = new Date(Date.UTC(year, 11, 31))

      return t.product.salesAndForecasts(
        {
          variantIds,
          warehouseId,
          channel,
          startDate,
          endDate,
        },
        {
          enabled: enabled && variantIds.length > 0,
        },
      )
    }),
  )

  const isLoading = yearQueries.some((q) => q.isLoading)
  const isError = yearQueries.some((q) => q.isError)

  const yearSeriesData = useMemo(() => {
    if (!enabled || isLoading) return []

    const today = startOfTodayUTC()

    return selectedYears.flatMap((year, _index) => {
      const queryData = yearQueries[_index]?.data
      const sales = queryData?.sales ?? []
      const rawForecasts = queryData?.rawForecasts ?? []

      // A comparison year without any actual sales (e.g. products launched later) would
      // render as a flat zero line — drop it instead of drawing a meaningless series.
      if (year !== referenceYear && !sales.some((s) => s.value !== 0)) return []

      // Opacity scales with how many years back this is from the reference year
      const yearsBack = referenceYear - year
      const style = getYearSeriesStyle(yearsBack)

      if (year === referenceYear) {
        // Past actuals: solid line at full opacity
        const processedPast = monthly ? aggregateToMonthly(sales) : sales
        const mappedPast = mapToSeason(processedPast, referenceYear, {
          granularity: monthly ? "month" : "week",
        })
        const normalizedPast = mappedPast
          .map((item) => ({ milliseconds: item.date.getTime(), value: Math.round(item.value) }))
          .sort((a, b) => a.milliseconds - b.milliseconds)

        const pastSeries: DateChartSeries = {
          name: `${formatName("sale", true, true)} ${year}`,
          data: normalizedPast,
          type: "spline",
          lineWidth: style.lineWidth,
          colors: [style.color],
          dashStyle: "Solid",
        }

        // Future demand/forecast: dashed, same opacity as 1-year-back
        const futureForecasts = rawForecasts.filter((f) => f.date > today)
        if (futureForecasts.length === 0) return [pastSeries]

        const processedFuture = monthly ? aggregateToMonthly(futureForecasts) : futureForecasts
        const mappedFuture = mapToSeason(processedFuture, referenceYear, {
          granularity: monthly ? "month" : "week",
        })
        const normalizedFuture = mappedFuture
          .map((item) => ({ milliseconds: item.date.getTime(), value: Math.round(item.value) }))
          .sort((a, b) => a.milliseconds - b.milliseconds)

        const demandStyle = getYearSeriesStyle(0, true)
        const demandSeries: DateChartSeries = {
          name: `Demand ${year}`,
          data: normalizedFuture,
          type: "spline",
          lineWidth: demandStyle.lineWidth,
          colors: [demandStyle.color],
          dashStyle: chartConfig.demand.dashStyle,
        }

        return [pastSeries, demandSeries]
      }

      // Historical year — single solid series, opacity by age
      const processedData = monthly ? aggregateToMonthly(sales) : sales
      const seasonMappedData = mapToSeason(processedData, referenceYear, {
        granularity: monthly ? "month" : "week",
      })
      const normalizedData = seasonMappedData
        .map((item) => ({ milliseconds: item.date.getTime(), value: Math.round(item.value) }))
        .sort((a, b) => a.milliseconds - b.milliseconds)

      const series: DateChartSeries = {
        name: `${formatName("sale", true, true)} ${year}`,
        data: normalizedData,
        type: "spline",
        lineWidth: style.lineWidth,
        colors: [style.color],
        dashStyle: "Solid",
      }

      return [series]
    })
  }, [
    enabled,
    isLoading,
    selectedYears,
    referenceYear,
    yearQueries,
    monthly,
    chartConfig,
    getYearSeriesStyle,
  ])

  // Calculate available years based on current year
  const availableYears = useMemo(() => {
    const currentYear = getYearUTC(startOfTodayUTC())
    // Show current year and 4 previous years
    return Array.from({ length: 5 }, (_, i) => currentYear - i)
  }, [])

  return {
    yearSeriesData,
    isLoading,
    isError,
    availableYears,
  }
}
